/**
 * The inbox as its view shows it (PRD-0075 R-B6, ADR-0096): the alerts read once, kept to the
 * filters on the page, counted for the filter chips, and grouped one row per rule and source.
 * Pure, so the page and the stories group fixtures the same way.
 */
import type { AlertGroupRow, AlertInboxRow } from '@/components/monitor/AlertInbox'
import { ALERT_STATES } from '@/components/monitor/AlertTriage'
import type { InboxFacets, InboxFilterValues } from '@/components/monitor/InboxFilters'
import { RULE_SEVERITIES } from '@/components/monitor/SeverityBadge'
import type { AlertDto, AlertSort, AlertState } from '@/services/api/case-monitor'
import { toTriagedRows } from './inbox-rows'

type Facet = keyof InboxFilterValues

/** A row's triage state; an alert of slice 06 (no triage fields) reads by its `isNew`. */
function stateOf(row: AlertInboxRow): AlertState | null {
  return row.triage ? row.triage.state : row.isNew ? 'new' : null
}

function kept(row: AlertInboxRow, filters: InboxFilterValues, skip?: Facet): boolean {
  return (
    (skip === 'state' || filters.state === null || stateOf(row) === filters.state) &&
    (skip === 'ruleId' || filters.ruleId === null || row.ruleId === filters.ruleId) &&
    (skip === 'documentId' || filters.documentId === null || row.documentId === filters.documentId)
  )
}

/** The rows the filters keep (all of them but `skip`, when given). */
export function keepRows(
  rows: readonly AlertInboxRow[],
  filters: InboxFilterValues,
  skip?: Facet
): AlertInboxRow[] {
  return rows.filter(row => kept(row, filters, skip))
}

function countBy<K>(rows: readonly AlertInboxRow[], key: (row: AlertInboxRow) => K | null) {
  const counts = new Map<K, { row: AlertInboxRow; count: number }>()
  for (const row of rows) {
    const value = key(row)
    if (value === null) continue
    const known = counts.get(value)
    counts.set(value, { row: known?.row ?? row, count: (known?.count ?? 0) + 1 })
  }
  return counts
}

/** How many alerts each chip keeps, the other filters applied. */
export function inboxFacets(
  rows: readonly AlertInboxRow[],
  filters: InboxFilterValues
): InboxFacets {
  const byState = keepRows(rows, filters, 'state')
  const byRule = keepRows(rows, filters, 'ruleId')
  const bySource = keepRows(rows, filters, 'documentId')
  const states = Object.fromEntries(
    ALERT_STATES.map(state => [state, byState.filter(row => stateOf(row) === state).length])
  ) as Record<AlertState, number>
  const rules = [...countBy(byRule, row => row.ruleId)].map(([id, { row, count }]) => ({
    id,
    name: row.ruleName,
    kind: row.ruleKind,
    count,
  }))
  const sources = [...countBy(bySource, row => row.documentId)].map(([id, { row, count }]) => ({
    id,
    name: row.documentName ?? id,
    count,
  }))
  return { states, anyState: byState.length, rules, anyRule: byRule.length, sources }
}

/** A row's group: its rule in its source, or the alert alone when it has no document (a merge). */
function groupKey(row: AlertInboxRow): string {
  return `${row.ruleId}:${row.documentId ?? row.id}`
}

function groupOf(key: string, alerts: AlertInboxRow[]): AlertGroupRow {
  const [first] = alerts
  return {
    key,
    ruleName: first.ruleName,
    ruleKind: first.ruleKind,
    severity: first.severity,
    documentId: first.documentId,
    documentName: first.documentName,
    learnedAt: alerts.reduce(
      (newest, alert) => (alert.learnedAt > newest ? alert.learnedAt : newest),
      first.learnedAt
    ),
    hits: alerts.reduce((sum, alert) => sum + alert.hits, 0),
    newCount: alerts.filter(alert => stateOf(alert) === 'new').length,
    isUnread: alerts.some(alert => alert.isUnread),
    alerts,
  }
}

/** Higher is more severe; a severity this build does not know sorts last. */
function severityRank(group: AlertGroupRow): number {
  return group.severity ? RULE_SEVERITIES.length - RULE_SEVERITIES.indexOf(group.severity) : 0
}

/**
 * One row per rule and source, its alerts in the order they came (newest learned first); an alert
 * of no document (a merge) is a row of its own. Newest first, or most severe first and then newest.
 */
export function groupAlerts(rows: readonly AlertInboxRow[], sort: AlertSort): AlertGroupRow[] {
  const groups = new Map<string, AlertInboxRow[]>()
  for (const row of rows) {
    const key = groupKey(row)
    const group = groups.get(key)
    if (group) group.push(row)
    else groups.set(key, [row])
  }
  return [...groups]
    .map(([key, alerts]) => groupOf(key, alerts))
    .sort((a, b) => {
      const bySeverity = sort === 'severity' ? severityRank(b) - severityRank(a) : 0
      return bySeverity || b.learnedAt.localeCompare(a.learnedAt)
    })
}

/**
 * The alerts read, as the inbox shows them: every row (for the chips), the groups the filters and
 * the order keep, and the chips' counts.
 */
export function inboxOf(
  alerts: readonly AlertDto[],
  unreadSince: string | null | undefined,
  filters: InboxFilterValues,
  sort: AlertSort
) {
  const rows = toTriagedRows(alerts, unreadSince)
  return {
    rows,
    groups: groupAlerts(keepRows(rows, filters), sort),
    facets: inboxFacets(rows, filters),
  }
}
